import { Chain } from "wagmi";
import { InjectedConnector } from "wagmi/connectors/injected";

export type InjectedConnectorOptions = {
  name?: string | ((detectedName: string | string[]) => string);
  shimDisconnect?: boolean;
};

export type ConnectorOptions = Pick<InjectedConnectorOptions, "shimDisconnect">;

export class BinanceWalletConnector extends InjectedConnector {
  readonly id = "binance";
  readonly ready =
    typeof window != "undefined" && !!this.#findProvider(window.BinanceChain);

  #provider?: Window["BinanceChain"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "Binance Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      // TODO: Fallback to ethereum#initialized event for async injection
      // https://github.com/MetaMask/detect-provider#synchronous-and-asynchronous-injection=
      this.#provider = this.#findProvider(window.BinanceChain);
    }
    return this.#provider;
  }

  #getReady(binance?: any) {
    if (!binance) return;

    return binance;
  }

  #findProvider(binance?: any) {
    if (binance?.providers) return binance.providers.find(this.#getReady);
    return this.#getReady(binance);
  }
}

export class Coins98WalletConnector extends InjectedConnector {
  readonly id = "coin98";
  readonly ready =
    typeof window != "undefined" &&
    !!this.#findProvider(window.coin98?.provider);

  #provider?: Window["coin98"]["provider"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "Coins98 Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      // TODO: Fallback to ethereum#initialized event for async injection
      // https://github.com/MetaMask/detect-provider#synchronous-and-asynchronous-injection=
      this.#provider = this.#findProvider(window.coin98?.provider);
    }
    return this.#provider;
  }

  #getReady(coins98?: any) {
    if (!coins98) return;

    return coins98;
  }

  #findProvider(coins98?: any) {
    if (coins98?.providers) return coins98.providers.find(this.#getReady);
    return this.#getReady(coins98);
  }
}

export class SafePalWalletConnector extends InjectedConnector {
  readonly id = "safepal";
  readonly ready =
    typeof window != "undefined" &&
    !!this.#findProvider(window.safepalProvider);

  #provider?: Window["safepalProvider"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "SafePal Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      this.#provider = this.#findProvider(window.safepalProvider);
    }
    return this.#provider;
  }

  #getReady(safepal?: any) {
    if (!safepal) return;

    return safepal;
  }

  #findProvider(safepal?: any) {
    if (safepal?.providers) return safepal.providers.find(this.#getReady);
    return this.#getReady(safepal);
  }
}

export class MathWalletConnector extends InjectedConnector {
  readonly id = "mathwallet";
  readonly ready =
    typeof window != "undefined" &&
    window.ethereum &&
    (window.ethereum as any).isMathWallet &&
    !!this.#findProvider(window.ethereum);

  #provider?: Window["ethereum"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "Math Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      this.#provider =
        window.ethereum && (window.ethereum as any).isMathWallet
          ? this.#findProvider(window.ethereum)
          : undefined;
    }
    return this.#provider;
  }

  #getReady(mathwallet?: any) {
    if (!mathwallet) return;

    return mathwallet;
  }

  #findProvider(mathwallet?: any) {
    if (mathwallet?.providers) return mathwallet.providers.find(this.#getReady);
    return this.#getReady(mathwallet);
  }
}

export class TrustWalletConnector extends InjectedConnector {
  readonly id = "trustwallet";
  readonly ready =
    typeof window != "undefined" && !!this.#findProvider(window.trustwallet);

  #provider?: Window["trustwallet"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "Trust Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      this.#provider = this.#findProvider(window.trustwallet);
    }
    return this.#provider;
  }

  #getReady(trustwallet?: any) {
    if (!trustwallet) return;

    return trustwallet;
  }

  #findProvider(trustwallet?: any) {
    if (trustwallet?.providers)
      return trustwallet.providers.find(this.#getReady);
    return this.#getReady(trustwallet);
  }
}

export class BitKeepConnector extends InjectedConnector {
  readonly id = "bitkeep";
  readonly ready =
    typeof window != "undefined" &&
    !!this.#findProvider(window.bitkeep?.ethereum);

  #provider?: Window["bitkeep"]["ethereum"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "BitKeep Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      this.#provider = this.#findProvider(window.window.bitkeep?.ethereum);
    }
    return this.#provider;
  }

  #getReady(bitkeep?: any) {
    if (!bitkeep) return;

    return bitkeep;
  }

  #findProvider(bitkeep?: any) {
    if (bitkeep?.providers) return bitkeep.providers.find(this.#getReady);
    return this.#getReady(bitkeep);
  }
}

export class OkexConnector extends InjectedConnector {
  readonly id = "okex";
  readonly ready =
    typeof window != "undefined" && !!this.#findProvider(window.okexchain);

  #provider?: Window["okexchain"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "OKX Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      this.#provider = this.#findProvider(window.okexchain);
    }
    return this.#provider;
  }

  #getReady(okex?: any) {
    if (!okex) return;

    return okex;
  }

  #findProvider(okex?: any) {
    if (okex?.providers) return okex.providers.find(this.#getReady);
    return this.#getReady(okex);
  }
}

export class NaboxConnector extends InjectedConnector {
  readonly id = "nabox";
  readonly ready =
    typeof window != "undefined" && !!this.#findProvider(window.NaboxWallet);

  #provider?: Window["NaboxWallet"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "Nabox Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      this.#provider = this.#findProvider(window.NaboxWallet);
    }
    return this.#provider;
  }

  #getReady(nabox?: any) {
    if (!nabox) return;

    return nabox;
  }

  #findProvider(nabox?: any) {
    if (nabox?.providers) return nabox.providers.find(this.#getReady);
    return this.#getReady(nabox);
  }
}
export class CoinhubConnector extends InjectedConnector {
  readonly id = "coinhub";
  readonly ready =
    typeof window != "undefined" && !!this.#findProvider(window.ethereum);

  #provider?: Window["ethereum"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "Coinhub Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      this.#provider = this.#findProvider(window.ethereum);
    }
    return this.#provider;
  }

  #getReady(coinhub?: any) {
    if (!coinhub) return;

    return coinhub;
  }

  #findProvider(coinhub?: any) {
    if (coinhub?.providers) return coinhub.providers.find(this.#getReady);
    return this.#getReady(coinhub);
  }
}

export class KuCoinWalletConnector extends InjectedConnector {
  readonly id = "kucoin";
  readonly ready =
    typeof window != "undefined" &&
    window.ethereum &&
    (window.ethereum as any).isKuCoinWallet &&
    !!this.#findProvider(window.ethereum);

  #provider?: Window["ethereum"];

  constructor(config?: { chains?: Chain[]; options?: ConnectorOptions }) {
    super({
      ...config,
      options: {
        name: "KuCoin Wallet",
        shimDisconnect: true,
        ...config?.options,
      },
    });
  }

  async getProvider() {
    if (typeof window !== "undefined") {
      this.#provider =
        typeof window != "undefined" &&
        window.ethereum &&
        (window.ethereum as any).isKuCoinWallet
          ? this.#findProvider(window.ethereum)
          : undefined;
    }
    return this.#provider;
  }

  #getReady(wallet?: any) {
    if (!wallet) return;

    return wallet;
  }

  #findProvider(wallet?: any) {
    if (wallet?.providers) return wallet.providers.find(this.#getReady);
    return this.#getReady(wallet);
  }
}
